import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Save, Sliders, Info, Clock, Wallet, BellOff, Sparkles, Globe, MapPin, RotateCcw } from "lucide-react";

const FIELDS = [
  { key: "activities_per_week", label: "Activities per week (PCWM)", min: 1, max: 20, section: "activity", help: "Below this each week, the Founder is nudged that the PCWM office is quiet." },
  { key: "activity_nudge_throttle_days", label: "Activity nudge cooldown (days)", min: 1, max: 14, section: "activity", help: "How many days between two activity nudges to the same office." },
  { key: "skip_streak_days", label: "Skip streak → Founder alert (days)", min: 1, max: 14, section: "activity", help: "Consecutive days a staffer can skip a task before the Founder is alerted." },
  { key: "social_videos_per_week", label: "Reels / videos per week (SMCM)", min: 1, max: 20, section: "social", help: "Weekly video quota SMCMs are expected to hit." },
  { key: "social_posts_per_week", label: "Posts per week (SMCM)", min: 1, max: 20, section: "social", help: "Weekly static-post quota SMCMs are expected to hit." },
  { key: "social_nudge_throttle_days", label: "Social nudge cooldown (days)", min: 1, max: 14, section: "social", help: "Days between two social-quota nudges to the same office." },
  { key: "stale_balance_days", label: "Stale balance email trigger (days)", min: 7, max: 120, section: "finance", help: "Partners with no new payment for this many days get one friendly nudge email." },
  { key: "silent_hours_start", label: "Silent hours start (24h)", min: 0, max: 23, section: "quiet", help: "Non-critical pushes are held after this hour so we never wake anyone up." },
  { key: "silent_hours_end", label: "Silent hours end (24h)", min: 0, max: 23, section: "quiet", help: "Pushes resume at this hour local." },
];

const SECTIONS = [
  { key: "activity", label: "PCWM activity", icon: Sparkles, tone: "border-blue-400" },
  { key: "social", label: "SMCM social quota", icon: Sparkles, tone: "border-fuchsia-400" },
  { key: "finance", label: "Partner finance nudge", icon: Wallet, tone: "border-emerald-500" },
  { key: "quiet", label: "Silent hours", icon: BellOff, tone: "border-amber-500" },
];

const OFFICES = [
  { key: "", label: "Global default", icon: Globe },
  { key: "India", label: "India office", icon: MapPin },
  { key: "Nepal", label: "Nepal office", icon: MapPin },
];

export default function NudgeThresholds() {
  const [office, setOffice] = useState("");
  const [vals, setVals] = useState({});
  const [initial, setInitial] = useState({});
  const [overrides, setOverrides] = useState({});
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      // Global fetch — merged view + `overrides` map so we can toggle office
      // tabs client-side without a second network call.
      const { data } = await api.get("/settings/nudge-thresholds");
      setOverrides(data.overrides || {});
      if (office) {
        const merged = { ...data };
        delete merged.overrides;
        Object.assign(merged, (data.overrides || {})[office] || {});
        setVals(merged); setInitial(merged);
      } else {
        const merged = { ...data };
        delete merged.overrides;
        setVals(merged); setInitial(merged);
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [office]);

  const set = (k, v) => setVals(prev => ({ ...prev, [k]: v }));
  const dirty = FIELDS.some(f => (vals[f.key] ?? "") !== (initial[f.key] ?? ""));
  const isOverride = office && Object.keys(overrides[office] || {}).length > 0;

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      for (const f of FIELDS) {
        const v = vals[f.key];
        if (v === "" || v === null || v === undefined) continue;
        payload[f.key] = Number(v);
      }
      if (office) payload.destination = office;
      await api.put("/settings/nudge-thresholds", payload);
      toast.success(office ? `${office} thresholds saved` : "Global thresholds saved");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const resetOffice = async () => {
    if (!office) return;
    if (!window.confirm(`Reset ${office} back to the global defaults? Its overrides will be removed.`)) return;
    setBusy(true);
    try {
      await api.delete(`/settings/nudge-thresholds/${encodeURIComponent(office)}`);
      toast.success(`${office} reset to global`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="p-6 text-sm text-[color:var(--aii-muted)]">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="nudge-thresholds-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand font-bold flex items-center gap-1"><Sliders size={11}/> Nudge thresholds</div>
          <h1 className="text-2xl font-display font-semibold mt-0.5">Automation dials</h1>
          <p className="text-sm text-[color:var(--aii-text-2)] mt-1 max-w-2xl">
            Tune how loud the system is. India and Nepal can operate on different rhythms — pick an office tab below to override the global defaults just for that team.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {office && isOverride && (
            <button data-testid="nt-reset" onClick={resetOffice} disabled={busy}
              className="text-xs px-3 py-2 border border-red-300 text-red-700 rounded-md hover:bg-red-50 font-semibold flex items-center gap-1.5">
              <RotateCcw size={12}/> Reset to global
            </button>
          )}
          <button data-testid="nt-save" onClick={save} disabled={busy || !dirty}
            className="text-sm px-4 py-2 bg-brand text-white rounded-md hover:bg-brand-dark font-semibold flex items-center gap-1.5 disabled:opacity-50">
            <Save size={14}/> {busy ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-[color:var(--aii-border)]" data-testid="nt-office-tabs">
        {OFFICES.map(o => {
          const Icon = o.icon;
          const hasOv = o.key && Object.keys(overrides[o.key] || {}).length > 0;
          const active = office === o.key;
          return (
            <button
              key={o.key || "global"}
              onClick={() => setOffice(o.key)}
              data-testid={`nt-office-${o.key || "global"}`}
              className={`px-3 py-2 text-sm font-semibold flex items-center gap-1.5 border-b-2 -mb-px transition ${active ? "border-brand text-brand" : "border-transparent text-[color:var(--aii-text-2)] hover:text-brand"}`}>
              <Icon size={13}/> {o.label}
              {hasOv && <span className="text-[9px] px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded font-bold">CUSTOM</span>}
            </button>
          );
        })}
      </div>

      {office && (
        <div className="text-xs bg-blue-50 border border-blue-200 text-blue-900 rounded px-3 py-2 flex items-start gap-1.5" data-testid="nt-office-hint">
          <Info size={11} className="mt-0.5 shrink-0"/>
          <span>Editing <b>{office}</b> office. Any value you save here overrides the global default for this office only. Fields you don't touch stay linked to global.</span>
        </div>
      )}

      {SECTIONS.map(section => (
        <div key={section.key} className={`aii-card p-5 border-l-4 ${section.tone}`} data-testid={`nt-section-${section.key}`}>
          <div className="flex items-center gap-2 mb-3">
            <section.icon size={14} className="text-[color:var(--aii-text-2)]"/>
            <h3 className="font-display font-semibold text-sm">{section.label}</h3>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {FIELDS.filter(f => f.section === section.key).map(f => (
              <div key={f.key}>
                <label className="text-[10px] uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold">{f.label}</label>
                <input data-testid={`nt-${f.key}`} type="number" min={f.min} max={f.max}
                  value={vals[f.key] ?? ""} onChange={e => set(f.key, e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm font-mono"/>
                <div className="text-[11px] text-[color:var(--aii-muted)] mt-1 flex items-start gap-1">
                  <Info size={10} className="flex-shrink-0 mt-0.5"/> {f.help}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="aii-card p-4 bg-[color:var(--aii-green-tint)]/30 flex items-start gap-2 text-xs text-[color:var(--aii-text-2)]">
        <Clock size={12} className="mt-0.5 text-brand flex-shrink-0"/>
        <div>
          Changes take effect on the next daily sweep — schedulers run every 24 hours. The silent-hours window is applied to every non-critical push (emergency alerts still go through).
        </div>
      </div>
    </div>
  );
}
